import { UNKNOWN_PROFILE_NAME } from '@/features/ai-models/modelsLedger';
import type {
  BreakdownEntry,
  DailyPart,
  WorkspaceUsageSummary,
} from '@/lib/api/generated/model';

import { magnitude } from './chartScale';
import { modelBreakdownLabel } from './modelBreakdownLabel';
import { sumMoney } from './money';
import {
  NO_PERSON_LABEL,
  shareSlices,
  type ShareLine,
  type Slice,
} from './shareSlices';

/**
 * The Per day bars, stacked (spec 257): which dimension a day splits by, and each
 * day's parts as the slices the share bars draw (spec 255), so the chart and the bars
 * below it speak about the same thing in the same colours.
 */

export type SplitDimension = 'workflow' | 'person' | 'profile' | 'model';

export const SPLIT_DIMENSIONS: readonly SplitDimension[] = [
  'workflow',
  'person',
  'profile',
  'model',
];

export const SPLIT_LABEL: Record<SplitDimension, string> = {
  workflow: 'Workflow',
  person: 'Person',
  profile: 'Profile',
  model: 'Model',
};

export function isSplitDimension(value: string): value is SplitDimension {
  return (SPLIT_DIMENSIONS as readonly string[]).includes(value);
}

/**
 * What a day may split by: what Where it went draws, but connection (spec 257, out of
 * scope); model only when the period has two or more, as its bar.
 */
export function splitOptions(
  summary: Pick<WorkspaceUsageSummary, 'by_model'>,
): SplitDimension[] {
  return SPLIT_DIMENSIONS.filter(
    (dimension) => dimension !== 'model' || summary.by_model.length >= 2,
  );
}

/** One slice's share of one day (or week): a segment of its bar. */
export interface StackPart {
  /** The slice's id: its line's key, "others", or "unknown-0". */
  id: string;
  label: string;
  amount: number;
  amountText: string;
  svgFill: string;
  /** Its slice names a deleted profile or workflow (spec 258). */
  deleted: boolean;
}

/** A period line and each day's parts, whichever read they came from. */
export interface SplitRead {
  /** The period's lines, keyed as the days' parts are. */
  lines: SplitLine[];
  days: { date: string; parts: DailyPart[] }[];
}

export interface SplitLine extends ShareLine {
  key: string | null;
}

/** A summary breakdown's lines as share lines, named as Where it went names them. */
export function summaryLines(
  dimension: Exclude<SplitDimension, 'profile'>,
  entries: BreakdownEntry[],
): SplitLine[] {
  return entries.map((entry) => ({
    key: entry.key,
    label:
      dimension === 'model'
        ? modelBreakdownLabel(entry)
        : dimension === 'person' && entry.key === null
          ? NO_PERSON_LABEL
          : entry.label,
    amount: entry.amount,
    tokens: entry.total_tokens,
    runs: entry.run_count,
    unmeasured: entry.unmeasured_run_count,
    unpriced: 0,
    unknown: entry.key === null,
  }));
}

/**
 * A profile line, "Not recorded" for the runs that named no profile (spec 252). A
 * deleted profile keeps its own line under its last name (spec 258).
 */
export function profileLine(line: {
  key: string | null;
  label: string;
  deleted: boolean;
  amount: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
  unpriced_run_count: number;
}): SplitLine {
  return {
    key: line.key,
    label: line.key === null ? UNKNOWN_PROFILE_NAME : line.label,
    amount: line.amount,
    tokens: line.total_tokens,
    runs: line.run_count,
    unmeasured: line.unmeasured_run_count,
    unpriced: line.unpriced_run_count,
    unknown: line.key === null,
    deleted: line.deleted,
  };
}

/** Each day's parts as segments, in the slices' order: the leading slice at the base. */
export function stacksByDate(read: SplitRead): {
  slices: Slice<SplitLine>[];
  stacks: Map<string, StackPart[]>;
} {
  const slices = shareSlices(read.lines);
  const sliceOf = new Map<string | null, Slice<SplitLine>>();
  for (const slice of slices)
    for (const line of slice.lines) sliceOf.set(line.key, slice);
  const order = new Map(slices.map((slice, index) => [slice.id, index]));
  const stacks = new Map<string, StackPart[]>();
  for (const day of read.days) {
    const merged = new Map<
      string,
      { slice: Slice<SplitLine>; amounts: string[] }
    >();
    for (const part of day.parts) {
      const slice = sliceOf.get(part.key);
      // A part whose line the period does not hold cannot be coloured as one: a run
      // recorded between the two reads. It joins nothing and is left to the day's
      // total, which still counts it.
      if (slice === undefined) continue;
      const entry = merged.get(slice.id) ?? { slice, amounts: [] };
      entry.amounts.push(part.amount);
      merged.set(slice.id, entry);
    }
    stacks.set(
      day.date,
      [...merged.values()]
        .sort(
          (a, b) => (order.get(a.slice.id) ?? 0) - (order.get(b.slice.id) ?? 0),
        )
        .map(({ slice, amounts }) => {
          const amountText = sumMoney(amounts) ?? '0';
          return {
            id: slice.id,
            label: slice.label,
            amount: magnitude(amountText),
            amountText,
            svgFill: slice.svgFill,
            deleted: slice.deleted,
          };
        }),
    );
  }
  return { slices, stacks };
}

/** A week's segments: each slice's days added up, in the slices' order. */
export function mergeStacks(stacks: (StackPart[] | undefined)[]): StackPart[] {
  const merged = new Map<string, { part: StackPart; amounts: string[] }>();
  for (const stack of stacks)
    for (const part of stack ?? []) {
      const entry = merged.get(part.id) ?? { part, amounts: [] };
      entry.amounts.push(part.amountText);
      merged.set(part.id, entry);
    }
  return [...merged.values()].map(({ part, amounts }) => {
    const amountText = sumMoney(amounts) ?? '0';
    return { ...part, amount: magnitude(amountText), amountText };
  });
}

/** What led a day: its biggest segment's name, or null for one plain bar. */
export function ledBy(stack: StackPart[] | undefined): string | null {
  if (stack === undefined || stack.length === 0) return null;
  return stack.reduce((top, part) => (part.amount > top.amount ? part : top))
    .label;
}
